import type { AuditSectionKey } from '@behuman/core-contract'
import { useEffect, useRef } from 'react'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

/**
 * Debounce before a section view is reported. Rapid tab-flipping through
 * sections never reports the ones the user skimmed past — only sections that
 * stay active this long count as viewed ("meaningful events, not a row per
 * click").
 */
export const AUDIT_REPORT_DEBOUNCE_MS = 1500

/**
 * PD-1322 (S8) — chart-frame-level section-view reporting. Mounted ONCE by
 * the chart section router (PatientChartBody in PatientPage.tsx), so every
 * registry section is reported without per-section opt-in. Reports at most
 * one event per section per chart visit; the reported-set resets when the
 * patient changes.
 *
 * The client only REPORTS what was viewed — the server authors the audit
 * record (actor, timestamp, source, result).
 */
export function useAuditSectionReporting(
  patientId: string | undefined,
  section: AuditSectionKey | undefined,
): void {
  const client = useClient()
  const reportedRef = useRef<Set<string>>(new Set())
  const patientRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (patientId !== patientRef.current) {
      patientRef.current = patientId
      reportedRef.current = new Set()
    }
    if (!patientId || !section) return
    if (reportedRef.current.has(section)) return

    const timer = setTimeout(() => {
      // Mark before the request so a slow response can't double-report;
      // unmark on failure so a retry is possible on the next visit.
      reportedRef.current.add(section)
      client.auditReportView({ patientId, sections: [section] }).catch((err) => {
        reportedRef.current.delete(section)
        logError('patient', 'Failed to report chart section view', err)
      })
    }, AUDIT_REPORT_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [client, patientId, section])
}
